<template>
  <modal :errors="errors" v-bind="$attrs" v-on="$listeners" width="60%">
    <template v-slot:title>
      <h1>
        {{ $t($i18nLabelsHelper(organizationType, "invite_new_teacher")) }}
      </h1>
    </template>
    <template v-slot:body>
      <el-form :model="form" :rules="rules" class="auth-form" ref="form">
        <h2>{{ $t($i18nLabelsHelper(organizationType, "invite_single_teacher")) }}</h2>
        <div class="form-row">
          <elsa-input
            class="first-name"
            :label="$t('company_labels.invite_teacher.firstname')"
            name="first_name"
            :placeholder="$t('company_labels.invite_teacher.firstname')"
            v-model.lazy="form.first_name"
          >
            {{ $t("organization_profile.edit_organization_modal.city_field.label") }}
          </elsa-input>
          <elsa-input
            class="last-name"
            :label="$t('company_labels.invite_teacher.last_name')"
            name="last_name"
            :placeholder="$t('company_labels.invite_teacher.last_name')"
            v-model.lazy="form.last_name"
          >
            {{ $t("organization_profile.edit_organization_modal.state_field.label") }}
          </elsa-input>
          <elsa-input
            :label="$t('login.email_field.label')"
            :placeholder="$t('login.email_field.placeholder')"
            class="email"
            name="email"
            v-model.trim.lazy="form.email"
          >
            {{ $t("login.email_field.label") }}
          </elsa-input>
        </div>
        <h2>{{ $t($i18nLabelsHelper(organizationType, "invite_multiple_teachers")) }}</h2>
        <div class="base-create-body">
          <picker :options="options" :picked.sync="picked" />
          <el-form v-if="picked === 'email-invite'">
            <emails-tags-input
              :label="$t('classes.create_class_flow.add_students.copy_paste_emails')"
              :placeholder="$t('classes.create_class_flow.add_students.insert_multiple_emails')"
              @updatedTags="setTags"
              ref="tags"
            />
          </el-form>
          <el-form class="uploader" name="fileList" v-if="picked === 'import-users'">
            <div class="upload-instructions">
              <p>{{ $t("organizations.invite_users.import.instructions2") }}</p>
              <a download href="/files/ELSA-Userlist.xlsx">{{
                $t("organizations.invite_users.import.get_template_xlsx")
              }}</a>
              <p>{{ $t("common.or") }}</p>
              <a download href="/files/ELSA-Userlist.csv">{{
                $t("organizations.invite_users.import.get_template_csv")
              }}</a>
              <p :style="{ 'margin-left': '0px' }">,</p>
              <p :style="{ 'margin-left': '0px' }">{{ $t("organizations.invite_users.import.instructions3") }}</p>
            </div>
            <slot v-if="hideUploader">
              {{ $t("organizations.invite_users.multiple_mail_to_invite", { numberOfInvites: numberOfEmails }) }}
            </slot>
            <el-upload
              :action="actionUrl"
              :auto-upload="true"
              :before-upload="getActionUrl"
              :drag="true"
              :file-list="form.fileList"
              :http-request="submitFile"
              :limit="1"
              :on-change="updateFileList"
              :on-remove="updateFileList"
              :on-success="onSuccess"
              :show-file-list="true"
              class="upload-files"
              v-bind:class="{ 'hide-uploader': hideUploader, 'hide-file-list': hideFileList }"
              v-loading="loading"
            >
              <i class="elsa-icon-import-grey"></i>
              <div class="el-upload__text">
                {{ $t("organizations.invite_users.import.instructions") }}
                <p>{{ $t("common.or") }}</p>
                <chip icon="">
                  {{ $t("organizations.invite_users.import.browse_files") }}
                </chip>
              </div>
            </el-upload>
          </el-form>
        </div>
        <div class="el-dialog__footer">
          <span class="dialog-footer elsa-button-footer">
            <tertiary-button @click.native="handleCancelClick">
              {{ $t("common.cancel") }}
            </tertiary-button>
            <primary-button @click.native="handleConfirmInvite" ref="submit">
              {{ $t("common.continue") }}
            </primary-button>
          </span>
        </div>
      </el-form>
    </template>
    <template v-slot:footer />
  </modal>
</template>

<script>
  import Modal from "@/components/common/Modal";
  import PrimaryButton from "@/components/common/buttons/PrimaryButton";
  import TertiaryButton from "@/components/common/buttons/TertiaryButton";
  import ElsaInput from "@/components/common/Input";
  import { handleFormValidation } from "@/helpers/formValidationHelper";
  import EmailsTagsInput from "@/components/common/EmailsTagsInput";
  import Chip from "@/components/common/Chip";
  import Picker from "@/components/common/Picker";
  import { mapActions } from "vuex";
  import { warningMessage } from "@/utils/messages";
  import { compose, pathOr, isEmpty, includes, length, defaultTo, path, map, objOf, pluck } from "ramda";
  import { TEACHER } from "@/constants/userRoles";
  import { readFile, uploadFile, allowedTypes } from "@/utils/uploader";

  export default {
    components: {
      Modal,
      PrimaryButton,
      TertiaryButton,
      ElsaInput,
      Chip,
      EmailsTagsInput,
      Picker,
    },
    props: {
      organizationType: {
        type: String,
      },
      organizationId: {},
      inviteUsers: {
        type: Function,
        default: () => {},
      },
    },
    data() {
      return {
        rules: {
          email: [
            {
              type: "email",
              message: this.$t('company_labels.invite_teacher.email_validation_msg'),
              required: false,
            },
          ],
        },
        picked: "email-invite",
        actionUrl: "",
        uploadId: null,
        loading: false,
        errors: null,
        numberOfEmails: 0,
        form: {
          newUsersEmails: [],
          newUsersFirstNames: [],
          newUsersLastNames: [],
          fileList: [],
        },
        validations: [
          {
            classes: "no-email",
            rule: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            disableAdd: true,
          },
        ],
        options: [
          {
            input: "email-invite",
            icon: "elsa-icon-copy-blue",
            iconActive: "elsa-icon-copy-blue",
            name: this.$t("organizations.invite_users.mail_invite"),
          },
          {
            input: "import-users",
            icon: "elsa-icon-import-black",
            iconActive: "elsa-icon-import",
            name: this.$t("organizations.invite_users.import_users"),
          },
        ],
      };
    },
    methods: {
      setTags(newEmails) {
        this.form = {
          ...this.form,
          newUsersEmails: pluck("text")(newEmails),
          newUsersFirstNames: [],
          newUsersLastNames: [],
        };
        const len = newEmails.length;
        this.form.newUsersFirstNames.length = len;
        this.form.newUsersLastNames.length = len;
      },
      handleConfirmInvite() {
        this.addUsers();
      },
      async addUsers() {
        this.loading = true;
        const {
          newUsersEmails,
          newUsersFirstNames,
          newUsersLastNames,
          email,
          first_name,
          last_name,
          selectedRole: role = TEACHER,
        } = this.form;
        try {
          if (email) {
            newUsersEmails.push(email);
            newUsersFirstNames.push(first_name || '');
            newUsersLastNames.push(last_name || '');
          }
          await this.inviteUsers(newUsersEmails, newUsersFirstNames, newUsersLastNames, role);
          this.loading = false;
          this.displaySuccess();
          try {
            this.$gtag.event(`${document.title}-teacher-invite`, {
              'category': `Send-Invite`,
              'label': `Add Teachers`
            });
          }
          catch (error) {
            error
          }
        } catch (error) {
          this.loading = false;
          this.errors = error;
        }
      },
      displaySuccess() {
        this.$message({
          message: this.$t("organizations.users_section.modals.invite_users.success"),
          center: true,
          customClass: "elsa-message full-width success",
          offset: 105,
          type: "success",
          iconClass: "elsa-icon-check-white",
        });

        this.resetModal();
      },
      handleCancelClick() {
        this.resetModal();
      },
      handleError(error) {
        this.errors = error;
      },
      resetModal() {
        this.errors = null;
        this.$refs.form.resetFields();
        this.$emit("closed");
      },
      async getActionUrl(file) {
        this.loading = true;
        this.errors = null;
        const fileSize = file.size;
        const contentType = file.type;
        const organizationId = this.organizationId;
        const groupId = null;
        const fileName = file.name;
        const role = TEACHER;
        const payload = {
          fileSize,
          contentType,
          organizationId,
          groupId,
          fileName,
          role,
        };

        if (!includes(contentType, allowedTypes)) {
          this.errors = true;
          this.updateFileList(null, []);
          this.loading = false;
          warningMessage(this.$t("common.file_type_not_allowed"))(this);
          return Promise.reject(false);
        }

        try {
          const req = await this.getInviteUploadUrl({
            ...payload,
          });

          this.actionUrl = req.data.presigned_url;
          this.uploadId = req.data.id;
        } catch (e) {
          this.errors = true;
          this.updateFileList(null, []);
          this.loading = false;
          warningMessage(this.$t("common.generic_error_message"))(this);
          return Promise.reject(false);
        }
      },
      async submitFile(request) {
        try {
          const { action: url, file } = request;
          const rawFile = await readFile(file);
          return await uploadFile(url, file.type, rawFile);
        } catch (e) {
          this.errors = true;
          this.loading = false;
          return warningMessage(this.$t("common.generic_error_message"))(this);
        }
      },
      async onSuccess() {
        try {
          const params = { is_flow_invite: 1 };
          const req = await this.getInviteUploadEmails({
            uploadId: this.uploadId,
            params,
          });

          if (compose(isEmpty, pathOr([], ["data", "emails"]))(req)) {
            this.loading = false;
            return warningMessage(this.$t("common.generic_error_message"))(this);
          }

          const emails = compose(map(objOf("text")), pathOr([], ["data", "emails"]))(req);
          this.setTags(emails);

          const first_names = compose(map(objOf("text")), pathOr([], ["data", "first_names"]))(req);
          const last_names = compose(map(objOf("text")), pathOr([], ["data", "last_names"]))(req);

          this.form = {
            ...this.form,
            newUsersFirstNames: pluck("text")(first_names),
            newUsersLastNames: pluck("text")(last_names),
          };

          this.numberOfEmails = compose(length, pathOr([], ["data", "emails"]))(req);
          this.form.fileReady = true;
          this.loading = false;
        } catch (e) {
          this.errors = true;
          this.loading = false;
          return warningMessage(this.$t("common.generic_error_message"))(this);
        }
      },
      updateFileList(file, fileList) {
        this.form.fileList = fileList;
      },
      ...mapActions("invite", ["getInviteUploadUrl", "getInviteUploadEmails", "updateInviteUploadEmails"]),
    },
    computed: {
      hideUploader() {
        return this.form.fileList.length > 0;
      },
      hideFileList() {
        return this.form.fileList.length === 0;
      },
      organizationName() {
        return compose(
          defaultTo(this.$store.getters["organization/currentOrganization"].name),
          path(["organizationData", "name"])
        )(this);
      },
    },
  };
</script>

<style lang="scss" scoped>
  .elsa-flat-button.el-button {
    padding: 0;
    margin: 0;
  }

  ::v-deep .el-form .el-form-item .el-form-item__label {
    text-transform: capitalize;
  }

  ::v-deep .el-form .el-dialog__footer {
    padding-top: 0;
  }

  .auth-form {
    .form-row {
      display: flex;

      .first-name {
        width: 25%;
        margin-left: 16px;
        margin-right: 16px;
      }

      .last-name {
        width: 25%;
        margin-right: 16px;
      }

      .email {
        width: 40%;
      }

      .el-form {
        padding-bottom: 0;
      }
    }

    h2 {
      font-weight: 500;
      font-size: 20px;
      line-height: 32px;
      padding-bottom: 20px;
    }
  }

  .elsa-button-footer {
    padding: 30px 0px 0px;
  }
</style>
